// Visor de documentos PDF en un modal centrado (notas de cobro, documentos de
// repartidores, evidencia de retiros). El S3 sirve los archivos con content-type
// octet-stream y content-disposition attachment, así que un link o un iframe directo
// SIEMPRE descarga: para verlo in-app se baja por fetch (el bucket responde
// access-control-allow-origin: *) y se monta como blob PDF en un iframe.
//
// Props:
//   open          boolean
//   url           URL del PDF (viene de la API: se valida con safeFileUrl)
//   title?        título del modal (por defecto el nombre del archivo o "Documento")
//   description?  línea secundaria bajo el título
//   filename?     nombre sugerido para la descarga
//   onClose       () => void
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, ExternalLink, FileText, FileX, RotateCw } from 'lucide-react';
import { Button } from './Button';
import { Modal } from './Modal';
import { EmptyState } from './EmptyState';
import { Skeleton } from './Skeleton';
import { safeFileUrl } from '../../utils/safeUrl';

// Alto del área del documento: el mismo para la carga, el error y el PDF, así el
// modal no salta de tamaño cuando el archivo termina de llegar.
const VIEWER_HEIGHT = 'h-[min(76vh,880px)]';

function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Carga con la forma de una hoja: nunca un spinner centrado sobre el vacío.
function PageSkeleton({ label }) {
  return (
    <div className={`${VIEWER_HEIGHT} flex justify-center overflow-hidden bg-bg-muted p-6`} role="status" aria-live="polite">
      <span className="sr-only">{label}</span>
      <div className="flex w-full max-w-[560px] flex-col gap-3 rounded-md border border-border bg-bg p-8 shadow-sm" aria-hidden="true">
        <Skeleton className="h-5 w-1/2" />
        <Skeleton className="h-3 w-1/3" />
        <div className="h-4" />
        {[100, 96, 92, 98, 70].map((w, i) => (
          <Skeleton key={i} className="h-3" style={{ width: `${w}%` }} />
        ))}
        <div className="h-4" />
        <Skeleton className="h-28 w-full" />
        {[94, 88, 60].map((w, i) => (
          <Skeleton key={`b${i}`} className="h-3" style={{ width: `${w}%` }} />
        ))}
      </div>
    </div>
  );
}

export function DocumentViewerModal({ open, url, title, description, filename, onClose }) {
  const { t } = useTranslation();
  const [blobUrl, setBlobUrl] = useState(null);
  const [size, setSize] = useState(0);
  const [error, setError] = useState(false);
  // Sube en cada "Reintentar" para volver a correr el fetch.
  const [attempt, setAttempt] = useState(0);
  // La URL viene de datos de la API: un esquema ejecutable (javascript:, data:) nunca
  // se baja, ni se monta en el iframe, ni se abre en pestaña nueva.
  const safeUrl = safeFileUrl(url);

  // Trae el PDF al abrir y libera el objectURL al cerrar/desmontar (si no, cada
  // apertura dejaría un blob huérfano en memoria durante toda la sesión).
  useEffect(() => {
    if (!open) return undefined;
    setBlobUrl(null);
    setSize(0);
    if (!safeUrl) {
      setError(true);
      return undefined;
    }
    setError(false);
    let cancelled = false;
    let createdUrl = null;
    (async () => {
      try {
        const res = await fetch(safeUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const buf = await res.arrayBuffer();
        if (cancelled) return;
        // El tipo se fuerza a application/pdf: el S3 lo entrega como octet-stream y
        // con ese tipo el iframe ofrecería descarga en vez de renderizar.
        createdUrl = URL.createObjectURL(new Blob([buf], { type: 'application/pdf' }));
        setBlobUrl(createdUrl);
        setSize(buf.byteLength);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
      // Al reabrir no debe verse ni un render con el blob ya revocado ni el error anterior.
      setBlobUrl(null);
      setError(false);
    };
  }, [open, safeUrl, attempt]);

  const heading = title || filename || t('Documento');
  const openInNewTab = useCallback(() => {
    if (safeUrl) window.open(safeUrl, '_blank', 'noopener');
  }, [safeUrl]);
  // Un <button> dentro de un <a download> es HTML inválido: la descarga se dispara
  // desde el botón con un enlace efímero sobre el blob ya cargado.
  const download = useCallback(() => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || 'documento.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [blobUrl, filename]);

  const meta = [t('PDF'), blobUrl ? formatBytes(size) : ''].filter(Boolean).join(' · ');

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={
        <span className="flex min-w-0 items-start gap-2">
          <FileText className="mt-0.5 h-5 w-5 shrink-0 text-fg-muted" aria-hidden="true" />
          {/* Sin truncar: en móvil el corte se comía justo el número del retiro. */}
          <span className="min-w-0 [overflow-wrap:anywhere]">{heading}</span>
        </span>
      }
      description={
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {description && <span>{description}</span>}
          {description && <span aria-hidden="true">·</span>}
          <span className="tabular-nums">{meta}</span>
        </span>
      }
      bodyClassName="p-0"
      footer={
        <>
          {/* Con error, "Abrir en pestaña nueva" ya está junto a "Reintentar" en el cuerpo. */}
          {!error && (
            <Button
              variant="ghost"
              iconLeft={<ExternalLink className="h-4 w-4" />}
              onClick={openInNewTab}
              disabled={!safeUrl}
            >
              {t('Abrir en pestaña nueva')}
            </Button>
          )}
          <Button
            variant="outline"
            iconLeft={<Download className="h-4 w-4" />}
            onClick={download}
            disabled={!blobUrl}
          >
            {t('Descargar')}
          </Button>
          <Button onClick={onClose}>{t('Cerrar')}</Button>
        </>
      }
    >
      {error ? (
        <div className={`${VIEWER_HEIGHT} flex items-center justify-center bg-bg-muted`}>
          <EmptyState
            icon={<FileX />}
            title={t('No se pudo mostrar el documento')}
            description={
              safeUrl
                ? t('Puede que la conexión se haya cortado o que el archivo ya no esté disponible.')
                : t('El enlace del documento no es válido.')
            }
            action={
              safeUrl && (
                <div className="flex flex-wrap justify-center gap-2">
                  <Button variant="outline" iconLeft={<RotateCw className="h-4 w-4" />} onClick={() => setAttempt((n) => n + 1)}>
                    {t('Reintentar')}
                  </Button>
                  <Button variant="ghost" iconLeft={<ExternalLink className="h-4 w-4" />} onClick={openInNewTab}>
                    {t('Abrir en pestaña nueva')}
                  </Button>
                </div>
              )
            }
          />
        </div>
      ) : !blobUrl ? (
        <PageSkeleton label={t('Cargando documento…')} />
      ) : (
        <iframe src={blobUrl} title={heading} className={`${VIEWER_HEIGHT} block w-full border-0 bg-bg-muted`} />
      )}
    </Modal>
  );
}

export default DocumentViewerModal;
